/*!
 * @license
 * Copyright © 2005-2024 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { DirectAccessUrlEntry, RenditionEntry, RenditionPaging, VersionEntry, VersionPaging } from '../model';
import { BaseApi } from './base.api';
import { throwIfNotDefined } from '../../../assert';
import { buildCollectionParam } from '../../../alfrescoApiClient';
export class VersionsApi extends BaseApi {
    createVersionRendition(nodeId, versionId, renditionBodyCreate) {
        throwIfNotDefined(nodeId, 'nodeId');
        throwIfNotDefined(versionId, 'versionId');
        throwIfNotDefined(renditionBodyCreate, 'renditionBodyCreate');
        const pathParams = {
            nodeId,
            versionId
        };
        return this.post({
            path: '/nodes/{nodeId}/versions/{versionId}/renditions',
            pathParams,
            bodyParam: renditionBodyCreate
        });
    }
    deleteVersion(nodeId, versionId) {
        throwIfNotDefined(nodeId, 'nodeId');
        throwIfNotDefined(versionId, 'versionId');
        const pathParams = {
            nodeId,
            versionId
        };
        return this.delete({
            path: '/nodes/{nodeId}/versions/{versionId}',
            pathParams
        });
    }
    getVersion(nodeId, versionId) {
        throwIfNotDefined(nodeId, 'nodeId');
        throwIfNotDefined(versionId, 'versionId');
        const pathParams = {
            nodeId,
            versionId
        };
        return this.get({
            path: '/nodes/{nodeId}/versions/{versionId}',
            pathParams,
            returnType: VersionEntry
        });
    }
    getVersionContent(nodeId, versionId, opts) {
        throwIfNotDefined(nodeId, 'nodeId');
        throwIfNotDefined(versionId, 'versionId');
        const pathParams = {
            nodeId,
            versionId
        };
        const queryParams = {
            attachment: opts === null || opts === void 0 ? void 0 : opts.attachment
        };
        const headerParams = {
            'If-Modified-Since': opts === null || opts === void 0 ? void 0 : opts.ifModifiedSince,
            Range: opts === null || opts === void 0 ? void 0 : opts.range
        };
        const accepts = ['application/octet-stream'];
        return this.get({
            path: '/nodes/{nodeId}/versions/{versionId}/content',
            pathParams,
            queryParams,
            headerParams,
            accepts,
            returnType: 'blob'
        });
    }
    getVersionRendition(nodeId, versionId, renditionId) {
        throwIfNotDefined(nodeId, 'nodeId');
        throwIfNotDefined(versionId, 'versionId');
        throwIfNotDefined(renditionId, 'renditionId');
        const pathParams = {
            nodeId,
            versionId,
            renditionId
        };
        return this.get({
            path: '/nodes/{nodeId}/versions/{versionId}/renditions/{renditionId}',
            pathParams,
            returnType: RenditionEntry
        });
    }
    getVersionRenditionContent(nodeId, versionId, renditionId, opts) {
        throwIfNotDefined(nodeId, 'nodeId');
        throwIfNotDefined(versionId, 'versionId');
        throwIfNotDefined(renditionId, 'renditionId');
        opts = opts || {};
        const pathParams = {
            nodeId,
            versionId,
            renditionId
        };
        const queryParams = {
            attachment: opts === null || opts === void 0 ? void 0 : opts.attachment,
            placeholder: opts === null || opts === void 0 ? void 0 : opts.placeholder
        };
        const headerParams = {
            'If-Modified-Since': opts === null || opts === void 0 ? void 0 : opts.ifModifiedSince,
            Range: opts === null || opts === void 0 ? void 0 : opts.range
        };
        const accepts = ['application/octet-stream'];
        return this.get({
            path: '/nodes/{nodeId}/versions/{versionId}/renditions/{renditionId}/content',
            pathParams,
            queryParams,
            headerParams,
            accepts,
            returnType: 'blob'
        });
    }
    listVersionHistory(nodeId, opts) {
        throwIfNotDefined(nodeId, 'nodeId');
        const pathParams = {
            nodeId
        };
        const queryParams = {
            include: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.include, 'csv'),
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv'),
            skipCount: opts === null || opts === void 0 ? void 0 : opts.skipCount,
            maxItems: opts === null || opts === void 0 ? void 0 : opts.maxItems
        };
        return this.get({
            path: '/nodes/{nodeId}/versions',
            pathParams,
            queryParams,
            returnType: VersionPaging
        });
    }
    listVersionRenditions(nodeId, versionId, opts) {
        throwIfNotDefined(nodeId, 'nodeId');
        throwIfNotDefined(versionId, 'versionId');
        const pathParams = {
            nodeId,
            versionId
        };
        return this.get({
            path: '/nodes/{nodeId}/versions/{versionId}/renditions',
            pathParams,
            queryParams: opts,
            returnType: RenditionPaging
        });
    }
    revertVersion(nodeId, versionId, revertBody, opts) {
        throwIfNotDefined(nodeId, 'nodeId');
        throwIfNotDefined(versionId, 'versionId');
        throwIfNotDefined(revertBody, 'revertBody');
        const pathParams = {
            nodeId,
            versionId
        };
        const queryParams = {
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.post({
            path: '/nodes/{nodeId}/versions/{versionId}/revert',
            pathParams,
            queryParams,
            bodyParam: revertBody,
            returnType: VersionEntry
        });
    }
    requestDirectAccessUrl(nodeId, versionId) {
        throwIfNotDefined(nodeId, 'nodeId');
        throwIfNotDefined(versionId, 'versionId');
        const pathParams = {
            nodeId,
            versionId
        };
        return this.post({
            path: '/nodes/{nodeId}/versions/{versionId}/request-direct-access-url',
            pathParams,
            returnType: DirectAccessUrlEntry
        });
    }
}
